/**
 * Unified Notifications Composable
 * Provides easy access to both toast and alert notifications
 */

import { useToast } from '@/plugins/toast'
import { useAlert } from '@/plugins/sweetalert'

/**
 * Composable for handling all notification types
 * @returns {Object} Notification methods
 */
export function useNotifications() {
  const toast = useToast()
  const alert = useAlert()
  
  return {
    // Toast notifications (non-blocking, auto-dismiss)
    toast,
    
    // Alert dialogs (blocking, requires user action)
    alert,
    
    // Convenience methods
    
    /**
     * Show a quick success notification
     * Uses toast for brief messages, alert for important ones
     */
    notifySuccess(message, options = {}) {
      if (options.important) {
        return alert.success('Success', message)
      }
      return toast.success(message, options)
    },
    
    /**
     * Show a quick error notification
     * Uses toast for brief messages, alert for important ones
     */
    notifyError(message, options = {}) {
      if (options.important) {
        return alert.error('Error', message)
      }
      return toast.error(message, options)
    },
    
    /**
     * Show a quick warning notification
     */
    notifyWarning(message, options = {}) {
      if (options.important) {
        return alert.warning('Warning', message)
      }
      return toast.warning(message, options)
    },
    
    /**
     * Show a quick info notification
     */
    notifyInfo(message, options = {}) {
      if (options.important) {
        return alert.info('Info', message)
      }
      return toast.info(message, options)
    },
    
    /**
     * Confirm an action with the user
     * @param {string} title - Confirmation title
     * @param {string} text - Confirmation message
     * @param {Object} options - Additional options
     * @returns {Promise<boolean>} - True if confirmed
     */
    async confirm(title, text = '', options = {}) {
      const result = await alert.confirm(title, text, options)
      return result.isConfirmed
    },
    
    /**
     * Confirm a delete action
     * @param {string} itemName - Name of item being deleted
     * @param {string} additionalText - Additional warning text
     * @returns {Promise<boolean>} - True if confirmed
     */
    async confirmDelete(itemName, additionalText = '') {
      const result = await alert.delete(
        `Delete ${itemName}?`,
        additionalText || 'This action cannot be undone.'
      )
      return result.isConfirmed
    },
    
    /**
     * Get text input from user
     * @param {string} title - Input dialog title
     * @param {Object} options - Additional options
     * @returns {Promise<string|null>} - Input value or null if cancelled
     */
    async promptInput(title, options = {}) {
      const result = await alert.input(title, options)
      return result.isConfirmed ? result.value : null
    },
    
    /**
     * Get textarea input from user
     * @param {string} title - Input dialog title
     * @param {Object} options - Additional options
     * @returns {Promise<string|null>} - Input value or null if cancelled
     */
    async promptTextarea(title, options = {}) {
      const result = await alert.textarea(title, options)
      return result.isConfirmed ? result.value : null
    },
    
    /**
     * Execute an async operation with loading state
     * Shows loading indicator and handles success/error
     */
    async withLoading(promise, options = {}) {
      const {
        loadingMessage = 'Processing...',
        successMessage,
        errorMessage,
        useToast: preferToast = true
      } = options
      
      if (preferToast) {
        return toast.promise(promise, {
          loading: loadingMessage,
          success: successMessage || 'Operation completed successfully',
          error: errorMessage || 'Operation failed'
        })
      }
      
      return alert.withLoading(promise, {
        loading: loadingMessage,
        success: successMessage,
        error: errorMessage
      })
    }
  }
}

export default useNotifications
